import React, {useCallback, useEffect, useMemo} from 'react';
import {useFormikContext} from 'formik';
import {useIntl} from 'react-intl';
import {distance} from 'fastest-levenshtein';
import TimiDataGrid from '../../../components/shared/grid';
import {
    GridColumnProps,
    GridGroupByProps,
    GridRowModel,
} from '../../../components/shared/grid/types';
import editCellStyles from '../../../components/shared/grid/components/edit-cell/edit-cell.module.scss';
import {
    GridDropdown,
    GridDropdownOption,
} from '../../../components/shared/grid/components/dropdown';
import PillSwitch from '../../../components/shared/pill-switch';
import {getAllEmployees} from '../../../features/employees/short';
import {getProjects} from '../../../features/projects/filter';
import {
    TimeTrackingImportDataMappingDto,
    TimeTrackingImportDataMappingTarget,
    TimeTrackingImportPreviewDto,
} from '../../../features/time-tracking-import/types';
import {
    getAllAbsencesTypes,
    getAllShiftsTypes,
    getAllTagsTypes,
} from '../../../features/types';
import {useAppDispatch, useAppSelector} from '../../../hooks/hooks';
import {Lx} from '../../../i18n/consts';
import {PROJECT_NAME_MAX_LENGTH} from '../../Projects/constants';
import {
    getDataMappingCandidates,
    getDataMappingKey,
    getDataMappingTargetLabel,
    TimeTrackingImportDataMappingCandidate,
} from '../utils/dataMappings';
import styles from '../time-tracking-import.module.scss';

interface TimeTrackingImportDataMappingSectionProps {
    preview: TimeTrackingImportPreviewDto;
    mappings: TimeTrackingImportDataMappingDto[];
    onChange: (mappings: TimeTrackingImportDataMappingDto[]) => void;
}

type MappingOption = {
    id: number;
    name: string;
};

type DataMappingGridRow = {
    id: string;
    index: number;
    candidate: TimeTrackingImportDataMappingCandidate;
    sourceValue: string;
    sourceValues: string[];
    targetLabel: string;
    rowsCount: number;
};

const DATA_MAPPING_TARGET_ORDER: TimeTrackingImportDataMappingTarget[] = [
    'project',
    'absenceType',
    'shiftType',
    'employee',
    'tag',
    'unknown',
];

const getDataMappingTargetOrder = (target: TimeTrackingImportDataMappingTarget) => {
    const order = DATA_MAPPING_TARGET_ORDER.indexOf(target);

    return order === -1 ? DATA_MAPPING_TARGET_ORDER.length : order;
};

const normalizeMatchValue = (value: string) =>
    value.trim().toLowerCase().replace(/\s+/g, ' ');

const getLimitedCreateTargetName = (
    target: TimeTrackingImportDataMappingTarget,
    targetName: string
) => target === 'project'
    ? targetName.slice(0, PROJECT_NAME_MAX_LENGTH)
    : targetName;

const getDataMappingGroupBy = (): GridGroupByProps => ({
    rowGroupingModel: ['targetLabel'],
    groupByRenderer: (_groupField, groupFieldValue) => (
        <div className={styles.dataMappingGridGroupRow}>{groupFieldValue}</div>
    ),
});

export default function TimeTrackingImportDataMappingSection({
    preview,
    mappings,
    onChange,
}: TimeTrackingImportDataMappingSectionProps) {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const {setFieldValue} = useFormikContext();
    const employees = useAppSelector((selector) => selector.rEmployees);
    const projects = useAppSelector((selector) => selector.eProjects);
    const shiftTypes = useAppSelector((selector) => selector.types.shifts.items);
    const absenceTypes = useAppSelector((selector) => selector.types.absences.items);
    const tagTypes = useAppSelector((selector) => selector.types.tags.items);

    useEffect(() => {
        if (employees.length < 1) {
            void dispatch(getAllEmployees());
        }

        if (projects.length < 1) {
            void dispatch(getProjects());
        }

        if (shiftTypes.length < 1) {
            void dispatch(getAllShiftsTypes());
        }

        if (absenceTypes.length < 1) {
            void dispatch(getAllAbsencesTypes());
        }

        if (tagTypes.length < 1) {
            void dispatch(getAllTagsTypes());
        }
    }, [
        absenceTypes.length,
        dispatch,
        employees.length,
        projects.length,
        shiftTypes.length,
        tagTypes.length,
    ]);

    const candidates = useMemo<TimeTrackingImportDataMappingCandidate[]>(
        () => getDataMappingCandidates(preview),
        [preview]
    );

    const candidateCollator = useMemo(
        () => new Intl.Collator(intl.locale, {sensitivity: 'base', numeric: true}),
        [intl.locale]
    );

    const orderedCandidates = useMemo(
        () => [...candidates].sort((left, right) => {
            const targetOrder = getDataMappingTargetOrder(left.target) - getDataMappingTargetOrder(right.target);

            if (targetOrder !== 0) {
                return targetOrder;
            }

            return candidateCollator.compare(left.sourceValue, right.sourceValue);
        }),
        [candidateCollator, candidates]
    );

    const dataMappingRows = useMemo<DataMappingGridRow[]>(
        () => orderedCandidates.map((candidate, index) => ({
            id: getDataMappingKey(candidate.field, candidate.sourceValue),
            index,
            candidate,
            sourceValue: candidate.sourceValue,
            sourceValues: candidate.sourceValues,
            targetLabel: getDataMappingTargetLabel(candidate.target, intl),
            rowsCount: candidate.rowNumbers.length,
        })),
        [intl, orderedCandidates]
    );

    const mappingByKey = useMemo(() => new Map(
        mappings.map((mapping) => [getDataMappingKey(mapping.field, mapping.sourceValue), mapping])
    ), [mappings]);

    useEffect(() => {
        let hasChanges = false;
        const nextMappings = mappings.map((mapping) => {
            if (mapping.action !== 'create' || mapping.target !== 'project' || !mapping.targetName) {
                return mapping;
            }

            const limitedTargetName = getLimitedCreateTargetName(mapping.target, mapping.targetName);
            if (limitedTargetName === mapping.targetName) {
                return mapping;
            }

            hasChanges = true;
            return {
                ...mapping,
                targetName: limitedTargetName,
            };
        });

        if (hasChanges) {
            onChange(nextMappings);
        }
    }, [mappings, onChange]);

    const getOptions = useCallback((target: TimeTrackingImportDataMappingTarget): MappingOption[] => {
        if (target === 'project') {
            return projects;
        }

        if (target === 'shiftType') {
            return shiftTypes;
        }

        if (target === 'absenceType') {
            return absenceTypes;
        }

        if (target === 'tag') {
            return tagTypes;
        }

        if (target === 'employee') {
            return employees;
        }

        return [];
    }, [absenceTypes, employees, projects, shiftTypes, tagTypes]);

    const getBestMappingOption = useCallback((sourceValue: string, options: MappingOption[]) => {
        const normalizedSource = normalizeMatchValue(sourceValue);

        if (options.length < 1) {
            return null;
        }

        return options
            .map((option) => {
                const normalizedOption = normalizeMatchValue(option.name);

                return {
                    option,
                    distance: distance(normalizedSource, normalizedOption),
                    lengthDelta: Math.abs(normalizedSource.length - normalizedOption.length),
                };
            })
            .sort((left, right) =>
                left.distance - right.distance
                || left.lengthDelta - right.lengthDelta
                || candidateCollator.compare(left.option.name, right.option.name)
            )[0]?.option ?? null;
    }, [candidateCollator]);

    useEffect(() => {
        orderedCandidates.forEach((candidate, index) => {
            const key = getDataMappingKey(candidate.field, candidate.sourceValue);
            const currentMapping = mappingByKey.get(key);
            const options = getOptions(candidate.target);
            const selectedTarget = options.find((option) => option.id === currentMapping?.targetId) ?? null;

            void setFieldValue(`dataMappingTarget-${index}`, selectedTarget, false);
            void setFieldValue(
                `dataMappingName-${index}`,
                currentMapping?.targetName ?? candidate.sourceValue,
                false
            );
        });
    }, [
        getOptions,
        mappingByKey,
        orderedCandidates,
        setFieldValue,
    ]);

    const upsertMapping = useCallback((
        candidate: TimeTrackingImportDataMappingCandidate,
        patch: Partial<TimeTrackingImportDataMappingDto>
    ) => {
        const key = getDataMappingKey(candidate.field, candidate.sourceValue);
        const currentMapping = mappingByKey.get(key) ?? {
            field: candidate.field,
            sourceValue: candidate.sourceValue,
            target: candidate.target,
            action: candidate.defaultAction,
            targetId: null,
            targetName: candidate.defaultAction === 'create' ? candidate.sourceValue : '',
        };
        const nextMapping: TimeTrackingImportDataMappingDto = {
            ...currentMapping,
            ...patch,
            target: candidate.target,
        };
        const limitedMapping = nextMapping.action === 'create'
            ? {
                ...nextMapping,
                targetName: getLimitedCreateTargetName(candidate.target, nextMapping.targetName ?? ''),
            }
            : nextMapping;
        const otherMappings = mappings.filter(
            (mapping) => getDataMappingKey(mapping.field, mapping.sourceValue) !== key
        );

        onChange([...otherMappings, limitedMapping]);
    }, [mappingByKey, mappings, onChange]);

    const dataMappingColumns = useMemo<GridColumnProps[]>(() => [
        {
            field: 'sourceValue',
            localizationKey: Lx.TimeTrackingImport.DATA_MAPPING_SOURCE_VALUE_COLUMN,
            isHeaderCollapseButton: true,
            flex: {grow: 2},
            cellClassName: styles.dataMappingGridMiddleCell,
            displayValue: (_value, row) => (
                <div className={styles.dataMappingGridSourceCell}>
                    <span>{(row as DataMappingGridRow).sourceValue}</span>
                    {(row as DataMappingGridRow).sourceValues.length > 1 ? (
                        <span className={styles.dataMappingGridSourceVariants}>
                            {intl.formatMessage(
                                {id: Lx.TimeTrackingImport.DATA_MAPPING_SOURCE_VARIANTS},
                                {values: (row as DataMappingGridRow).sourceValues.join(', ')}
                            )}
                        </span>
                    ) : null}
                </div>
            ),
        },
        {
            field: 'rowsCount',
            localizationKey: Lx.TimeTrackingImport.DATA_MAPPING_ROWS_COLUMN,
            width: 96,
            cellClassName: styles.dataMappingGridMiddleCell,
            displayValue: (_value, row) => (
                <div className={styles.dataMappingGridTextCell}>
                    {intl.formatMessage(
                        {id: Lx.TimeTrackingImport.DATA_MAPPING_ROWS_COUNT},
                        {count: (row as DataMappingGridRow).rowsCount}
                    )}
                </div>
            ),
        },
        {
            field: 'action',
            localizationKey: Lx.TimeTrackingImport.DATA_MAPPING_ACTION_COLUMN,
            width: 260,
            cellRender: (_field, row) => {
                const {candidate, index} = row as DataMappingGridRow;
                const key = getDataMappingKey(candidate.field, candidate.sourceValue);
                const currentMapping = mappingByKey.get(key);
                const options = getOptions(candidate.target);
                const action = currentMapping?.action ?? (
                    options.length < 1 ? 'create' : candidate.defaultAction
                );
                const bestOption = getBestMappingOption(candidate.sourceValue, options);
                const targetFieldName = `dataMappingTarget-${index}`;

                return (
                    <div className={styles.dataMappingGridControlCell}>
                        <PillSwitch
                            value={action}
                            options={[
                                {
                                    value: 'map',
                                    label: intl.formatMessage({
                                        id: Lx.TimeTrackingImport.DATA_MAPPING_USE_EXISTING,
                                    }),
                                    disabled: options.length < 1,
                                },
                                {
                                    value: 'create',
                                    label: intl.formatMessage({
                                        id: Lx.TimeTrackingImport.DATA_MAPPING_CREATE_NEW,
                                    }),
                                },
                            ]}
                            onChange={(nextAction) => {
                                if (nextAction === 'map') {
                                    void setFieldValue(targetFieldName, bestOption, false);
                                    upsertMapping(candidate, {
                                        action: 'map',
                                        targetId: bestOption?.id ?? null,
                                        targetName: bestOption?.name ?? '',
                                    });
                                    return;
                                }

                                upsertMapping(candidate, {
                                    action: 'create',
                                    targetId: null,
                                    targetName: candidate.sourceValue,
                                });
                            }}
                        />
                    </div>
                );
            },
        },
        {
            field: 'mapping',
            localizationKey: Lx.TimeTrackingImport.DATA_MAPPING_MAPPING_COLUMN,
            flex: {grow: 3},
            cellRender: (_field, row) => {
                const {candidate, index} = row as DataMappingGridRow;
                const key = getDataMappingKey(candidate.field, candidate.sourceValue);
                const currentMapping = mappingByKey.get(key);
                const options = getOptions(candidate.target);
                const action = currentMapping?.action ?? (
                    options.length < 1 ? 'create' : candidate.defaultAction
                );
                const targetFieldName = `dataMappingTarget-${index}`;
                const nameFieldName = `dataMappingName-${index}`;
                const targetLabel = getDataMappingTargetLabel(candidate.target, intl).toLowerCase();
                const newTargetNameLabel = intl.formatMessage(
                    {id: Lx.TimeTrackingImport.DATA_MAPPING_NEW_TARGET_NAME},
                    {target: targetLabel}
                );
                const isProjectCreate = candidate.target === 'project';
                const createTargetName = getLimitedCreateTargetName(
                    candidate.target,
                    currentMapping?.targetName ?? candidate.sourceValue
                );
                const targetError = action === 'map' && !currentMapping?.targetId
                    ? 'required'
                    : undefined;

                if (action === 'map') {
                    return (
                        <div className={styles.dataMappingGridDropdownCell}>
                            <GridDropdown
                                multiple={false}
                                value={currentMapping?.targetId ?? null}
                                aria-label={getDataMappingTargetLabel(candidate.target, intl)}
                                aria-invalid={targetError ? true : undefined}
                                onChange={(_, value) => {
                                    const item = options.find((option) => option.id === value) ?? null;
                                    void setFieldValue(targetFieldName, item, false);
                                    upsertMapping(candidate, {
                                        action: 'map',
                                        targetId: item?.id ?? null,
                                        targetName: item?.name ?? '',
                                    });
                                }}
                            >
                                {options.length > 0 ? options.map((item) => (
                                    <GridDropdownOption key={item.id} value={item.id}>
                                        {item.name}
                                    </GridDropdownOption>
                                )) : (
                                    <GridDropdownOption value={0} disabled>
                                        {intl.formatMessage({
                                            id: Lx.TimeTrackingImport.DATA_MAPPING_SELECT_REPLACEMENT,
                                        })}
                                    </GridDropdownOption>
                                )}
                            </GridDropdown>
                        </div>
                    );
                }

                return (
                    <div className={styles.dataMappingGridTextInputCell}>
                        <textarea
                            name={nameFieldName}
                            aria-label={newTargetNameLabel}
                            placeholder={newTargetNameLabel}
                            className={`${editCellStyles.text} ${styles.dataMappingGridTextarea}`}
                            value={createTargetName}
                            maxLength={isProjectCreate ? PROJECT_NAME_MAX_LENGTH : undefined}
                            onChange={(event) => {
                                const nextValue = getLimitedCreateTargetName(candidate.target, event.target.value);
                                void setFieldValue(nameFieldName, nextValue, false);
                                upsertMapping(candidate, {
                                    action: 'create',
                                    targetId: null,
                                    targetName: nextValue,
                                });
                            }}
                        />
                    </div>
                );
            },
        },
    ], [
        getBestMappingOption,
        getOptions,
        intl,
        mappingByKey,
        setFieldValue,
        upsertMapping,
    ]);

    const dataMappingGroupBy = useMemo(() => getDataMappingGroupBy(), []);
    // Mapping groups are expanded by default: the values that have to be resolved must be visible
    // right away, without a click on every group.
    const dataMappingGroupKeys = useMemo(
        () => Array.from(new Set(dataMappingRows.map((row) => row.targetLabel))),
        [dataMappingRows]
    );

    if (candidates.length < 1) {
        return (
            <section className={styles.dataMappingSection}>
                <div className={styles.cardHeader}>
                    <div>
                        <h3>{intl.formatMessage({id: Lx.TimeTrackingImport.DATA_MAPPING})}</h3>
                        <p>{intl.formatMessage({id: Lx.TimeTrackingImport.DATA_MAPPING_NO_UNRESOLVED})}</p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className={styles.dataMappingSection}>
            <div className={styles.cardHeader}>
                <div>
                    <h3>{intl.formatMessage({id: Lx.TimeTrackingImport.DATA_MAPPING})}</h3>
                    <p>{intl.formatMessage({id: Lx.TimeTrackingImport.DATA_MAPPING_DESCRIPTION})}</p>
                </div>
            </div>
            <div className={styles.dataMappingGridWrapper}>
                <TimiDataGrid
                    columns={dataMappingColumns}
                    rows={dataMappingRows as GridRowModel[]}
                    groupBy={dataMappingGroupBy}
                    intl={intl}
                    className={styles.dataMappingGrid}
                    headerClassName={styles.dataMappingGridHeader}
                    bodyClassName={styles.dataMappingGridBody}
                    disableBodyScroll
                    initialExpandedRowKeys={dataMappingGroupKeys}
                />
            </div>
        </section>
    );
}
